import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Phone, PhoneCall, PhoneOutgoing, PhoneOff,
    MessageSquare, ChevronLeft, RefreshCw, Plus, Mail,
    Clock, MapPin, CheckCircle, Search, Volume2, Loader2,
    Trash2, Square, CheckSquare, X, User, Settings, Bot, Sparkles, DollarSign
} from 'lucide-react';
import {
    fetchCommunicationChains,
    createCommunicationChain,
    addCommunicationChainEvent,
    updateCommunicationChainStatus,
    markCommunicationChainResolved,
    recordCommunicationEvent,
    sendEmailReply,
    suggestEmailReply,
    quoteServices,
    initializeTwilioDevice,
    makeCall,
    hangupCall,
    formatPhoneNumber,
    getStatusColor,
    getEventTypeInfo,
    getVoicemailAudioUrl,
    bulkUpdateCommunicationChains,
    COMMUNICATION_CHAIN_STATUS,
    COMMUNICATION_EVENT_TYPES,
} from '../services/communicationCenterService';
import { searchClients, DAYSMART_LOCATIONS } from '../services/daysmartService';
import LoadingSpinner from '../LoadingSpinner';
import ChainDetailModal from '../components/modals/ChainDetailModal';
import ClientLinkNotice from '../components/ClientLinkNotice';
import { isWeakNameLink, senderLabel } from '../utils/clientLink';
import ClientAppointmentsPanel from '../components/ClientAppointmentsPanel';
import ZoomBotAppointmentsPanel from '../components/ZoomBotAppointmentsPanel';
import StickyAvailabilityDrawer from '../components/StickyAvailabilityDrawer';
import QuoteResultModal from '../components/modals/QuoteResultModal';
import useUserStore from '../stores/userStore';
import { getEmailEventContent, isManualEmailNote } from '../utils/emailDisplayUtils';

const COLLAPSED_TIMELINE_EVENT_LIMIT = 3;
const CHAIN_PAGE_SIZE = 50;
const CHAIN_SEARCH_DEBOUNCE_MS = 300;

const VoicemailPlayer = ({ blobName, bucket, label }) => {
    const [audioUrl, setAudioUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handlePlay = async () => {
        if (audioUrl) return; // already loaded
        setLoading(true);
        setError(null);
        try {
            const url = await getVoicemailAudioUrl(blobName, bucket);
            setAudioUrl(url);
        } catch (err) {
            setError('Failed to load audio');
        } finally {
            setLoading(false);
        }
    };

    if (!blobName) return null;

    return (
        <div className="mt-1">
            {!audioUrl && !loading && !error && (
                <button
                    onClick={handlePlay}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                    <Volume2 className="w-3.5 h-3.5" />
                    {label || 'Play voicemail'}
                </button>
            )}
            {loading && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading audio...
                </span>
            )}
            {error && <span className="text-xs text-red-500">{error}</span>}
            {audioUrl && (
                <audio controls autoPlay className="h-8 w-full max-w-xs">
                    <source src={audioUrl} type="audio/wav" />
                </audio>
            )}
        </div>
    );
};

const CommunicationCenter = () => {
    const navigate = useNavigate();
    const { currentUser, employees: storeEmployees } = useUserStore();
    
    // Tab state
    const [activeTab, setActiveTab] = useState('call_chains'); // 'call_chains', 'email_chains', 'groombot', 'new'
    
    // Location filter
    const [selectedLocation, setSelectedLocation] = useState(DAYSMART_LOCATIONS.ALL);
    
    // Loading states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Communication chains data
    const [communicationChains, setCommunicationChains] = useState([]);
    const [hasMoreChains, setHasMoreChains] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [openCallCount, setOpenCallCount] = useState(0);
    const [openEmailCount, setOpenEmailCount] = useState(0);
    const [activeStatusFilters, setActiveStatusFilters] = useState(
        new Set([COMMUNICATION_CHAIN_STATUS.OPEN, COMMUNICATION_CHAIN_STATUS.PENDING_RESPONSE])
    );
    const [expandedChain, setExpandedChain] = useState(null);
    const [expandedTimelineChains, setExpandedTimelineChains] = useState(new Set());
    const [selectedChains, setSelectedChains] = useState(new Set());
    const [clientFilter, setClientFilter] = useState(null); // { clientId, clientName, clientPhone } or null
    const [chainSearchTerm, setChainSearchTerm] = useState('');
    const [debouncedChainSearch, setDebouncedChainSearch] = useState('');
    const loadMoreSentinelRef = useRef(null);
    const chainOffsetRef = useRef(0);
    const hasMoreRef = useRef(true);
    const loadingMoreRef = useRef(false);
    const loadingRef = useRef(false);
    const loadRequestIdRef = useRef(0);
    const loadedChainIdsRef = useRef(new Set());
    const chainSearchTermRef = useRef('');
    
    // New chain form
    const [newChainForm, setNewChainForm] = useState({
        clientName: '',
        clientPhone: '',
        clientId: null,
        notes: '',
        eventType: COMMUNICATION_EVENT_TYPES.INBOUND_CALL,
        eventNotes: '',
    });
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [clientSearchResults, setClientSearchResults] = useState([]);
    const [searchingClients, setSearchingClients] = useState(false);
    
    // Add event modal
    const [showAddEventModal, setShowAddEventModal] = useState(false);
    const [addEventChainId, setAddEventChainId] = useState(null);
    const [newEventType, setNewEventType] = useState(COMMUNICATION_EVENT_TYPES.OUTBOUND_CALL);
    const [newEventNotes, setNewEventNotes] = useState('');
    
    // Chain detail modal
    const [detailChain, setDetailChain] = useState(null);
    
    // Sticky availability drawer
    const [availabilityDrawerOpen, setAvailabilityDrawerOpen] = useState(false);
    const [availabilityBookingContext, setAvailabilityBookingContext] = useState(null);
    
    // Email reply modal
    const [showEmailReplyModal, setShowEmailReplyModal] = useState(false);
    const [emailReplyChainId, setEmailReplyChainId] = useState(null);
    const [emailReplyTo, setEmailReplyTo] = useState('');
    const [emailReplySubject, setEmailReplySubject] = useState('');
    const [emailReplyBody, setEmailReplyBody] = useState('');
    const [emailReplyInReplyTo, setEmailReplyInReplyTo] = useState(null);
    const [emailReplyFrom, setEmailReplyFrom] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [suggestingReply, setSuggestingReply] = useState(false);
    const [suggestionError, setSuggestionError] = useState(null);
    const [emailModalPos, setEmailModalPos] = useState(null);
    const emailModalRef = useRef(null);
    const emailModalDrag = useRef({ active: false, ox: 0, oy: 0 });

    // Quote modal state
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const [quoteData, setQuoteData] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState(null);

    const handleEmailModalDragStart = (e) => {
        e.preventDefault();
        if (!emailModalRef.current) return;
        const rect = emailModalRef.current.getBoundingClientRect();
        emailModalDrag.current = { active: true, ox: e.clientX - rect.left, oy: e.clientY - rect.top };
        const onMove = (ev) => {
            if (!emailModalDrag.current.active) return;
            setEmailModalPos({ x: ev.clientX - emailModalDrag.current.ox, y: ev.clientY - emailModalDrag.current.oy });
        };
        const onUp = () => {
            emailModalDrag.current.active = false;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };
    
    // Browser calling state
    const [callStatus, setCallStatus] = useState(null); // null, 'initializing', 'ready', 'ringing', 'connected', 'disconnected'
    const [activeCallPhone, setActiveCallPhone] = useState(null);
    const [twilioInitialized, setTwilioInitialized] = useState(false);
    
    // Initialize Twilio Device on mount
    useEffect(() => {
        const initTwilio = async () => {
            if (currentUser?.user_id && !twilioInitialized) {
                try {
                    setCallStatus('initializing');
                    await initializeTwilioDevice(`agent_${currentUser.user_id}`);
                    setTwilioInitialized(true);
                    setCallStatus('ready');
                } catch (err) {
                    console.error('Failed to initialize Twilio:', err);
                    setCallStatus(null);
                }
            }
        };
        initTwilio();
    }, [currentUser?.user_id, twilioInitialized]);
    
    // Toggle a status filter on/off
    const toggleStatusFilter = (status) => {
        setActiveStatusFilters(prev => {
            const next = new Set(prev);
            if (next.has(status)) {
                // Don't allow deselecting the last active filter
                if (next.size > 1) next.delete(status);
            } else {
                next.add(status);
            }
            return next;
        });
    };

    const allStatuses = [COMMUNICATION_CHAIN_STATUS.OPEN, COMMUNICATION_CHAIN_STATUS.PENDING_RESPONSE, COMMUNICATION_CHAIN_STATUS.RESOLVED];
    const allFiltersActive = allStatuses.every(s => activeStatusFilters.has(s));
    const statusFilterKey = [...activeStatusFilters].sort().join(',');
    const isChainTab = activeTab === 'call_chains' || activeTab === 'email_chains';

    // Instant client-side filter of already-loaded results while a server search is in flight
    const filteredChains = communicationChains.filter(chain => {
        if (!activeStatusFilters.has(chain.status)) return false;
        if (clientFilter) {
            const matchesId = clientFilter.clientId && chain.client_id === clientFilter.clientId;
            const matchesPhone = clientFilter.clientPhone && chain.client_phone && 
                chain.client_phone.replace(/\D/g, '').includes(clientFilter.clientPhone.replace(/\D/g, ''));
            if (!matchesId && !matchesPhone) return false;
        }
        if (chainSearchTerm.trim()) {
            const term = chainSearchTerm.trim().toLowerCase();
            const nameMatch = chain.client_name?.toLowerCase().includes(term)
                || senderLabel(chain).toLowerCase().includes(term)
                || chain.suggested_client?.client_name?.toLowerCase().includes(term)
                || (chain.client_match_candidates || []).some((candidate) => candidate.client_name?.toLowerCase().includes(term));
            const emailMatch = chain.client_email?.toLowerCase().includes(term)
                || chain.sender_email?.toLowerCase().includes(term);
            const termDigits = term.replace(/\D/g, '');
            const phoneMatch = termDigits.length >= 3 && chain.client_phone?.replace(/\D/g, '').includes(termDigits);
            const notesMatch = chain.notes?.toLowerCase().includes(term);
            if (!nameMatch && !emailMatch && !phoneMatch && !notesMatch) return false;
        }
        return true;
    });

    const filteredCallChains = filteredChains.filter(c => c.chain_type !== 'email');
    const filteredEmailChains = filteredChains.filter(c => c.chain_type === 'email');
    const displayedChains = activeTab === 'email_chains' ? filteredEmailChains : filteredCallChains;
    const searchPending = chainSearchTerm.trim() !== debouncedChainSearch;
    const getEventsNewestFirst = (events = []) => [...events].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    const getCommunicationEventInfo = (event) => isManualEmailNote(event)
        ? { label: 'Logged Note', icon: '📝', color: 'text-gray-600' }
        : getEventTypeInfo(event.type);
    const toggleTimelineExpansion = (chainId) => {
        setExpandedTimelineChains(prev => {
            const next = new Set(prev);
            if (next.has(chainId)) {
                next.delete(chainId);
            } else {
                next.add(chainId);
            }
            return next;
        });
    };

    const loadCommunicationChains = useCallback(async ({ append = false } = {}) => {
        if (!isChainTab) return;
        if (append) {
            if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
            if (chainSearchTermRef.current.trim() !== debouncedChainSearch) return;
            loadingMoreRef.current = true;
            setLoadingMore(true);
        } else {
            loadingMoreRef.current = false;
            loadingRef.current = true;
            setLoadingMore(false);
            setLoading(true);
            chainOffsetRef.current = 0;
            hasMoreRef.current = true;
        }

        const requestId = ++loadRequestIdRef.current;
        const offset = append ? chainOffsetRef.current : 0;
        setError(null);
        try {
            const result = await fetchCommunicationChains({
                locationId: selectedLocation || undefined,
                status: statusFilterKey || undefined,
                search: debouncedChainSearch || undefined,
                offset,
                limit: CHAIN_PAGE_SIZE,
                onlyChainType: activeTab === 'email_chains' ? 'email' : undefined,
                excludeChainType: activeTab === 'call_chains' ? 'email' : undefined,
                clientId: clientFilter?.clientId || undefined,
                clientPhone: clientFilter?.clientPhone || undefined,
            });
            if (requestId !== loadRequestIdRef.current) return;

            const incoming = result.chains || [];
            const newOnes = append
                ? incoming.filter(c => !loadedChainIdsRef.current.has(c.chain_id))
                : incoming;

            if (!append) {
                loadedChainIdsRef.current = new Set(incoming.map(c => c.chain_id));
                setCommunicationChains(incoming);
            } else if (newOnes.length > 0) {
                newOnes.forEach(c => loadedChainIdsRef.current.add(c.chain_id));
                setCommunicationChains(prev => [...prev, ...newOnes]);
            }

            chainOffsetRef.current = offset + incoming.length;
            let hasMore = typeof result.has_more === 'boolean'
                ? result.has_more
                : incoming.length >= CHAIN_PAGE_SIZE;
            if (append && newOnes.length === 0) hasMore = false;
            hasMoreRef.current = hasMore;
            setHasMoreChains(hasMore);

            if (typeof result.open_call_count === 'number') setOpenCallCount(result.open_call_count);
            if (typeof result.open_email_count === 'number') setOpenEmailCount(result.open_email_count);
        } catch (err) {
            if (requestId !== loadRequestIdRef.current) return;
            setError(err.message);
            if (append) {
                hasMoreRef.current = false;
                setHasMoreChains(false);
            }
        } finally {
            if (requestId === loadRequestIdRef.current) {
                if (append) {
                    loadingMoreRef.current = false;
                    setLoadingMore(false);
                } else {
                    loadingRef.current = false;
                    setLoading(false);
                }
            }
        }
    }, [
        isChainTab,
        activeTab,
        selectedLocation,
        statusFilterKey,
        debouncedChainSearch,
        clientFilter,
    ]);

    useEffect(() => {
        chainSearchTermRef.current = chainSearchTerm;
        const handle = setTimeout(() => {
            setDebouncedChainSearch(chainSearchTerm.trim());
        }, CHAIN_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [chainSearchTerm]);

    // Reset and fetch the first page when tab, filters, location, or search change
    useEffect(() => {
        if (isChainTab) {
            setCommunicationChains([]);
            setSelectedChains(new Set());
            loadCommunicationChains({ append: false });
        }
    }, [activeTab, isChainTab, loadCommunicationChains]);

    // Load older chains when the user scrolls near the bottom
    useEffect(() => {
        if (!isChainTab) return;
        const node = loadMoreSentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver((entries) => {
            if (!entries[0]?.isIntersecting) return;
            loadCommunicationChains({ append: true });
        }, { root: null, rootMargin: '400px', threshold: 0 });
        observer.observe(node);
        return () => observer.disconnect();
    }, [isChainTab, loadCommunicationChains, displayedChains.length, hasMoreChains]);
    
    // Client search for new chain
    const handleClientSearch = async () => {
        if (!clientSearchTerm.trim()) return;
        
        setSearchingClients(true);
        try {
            const result = await searchClients(
                clientSearchTerm,
                'all',
                selectedLocation || undefined
            );
            setClientSearchResults(result.clients || []);
        } catch (err) {
            console.error('Client search error:', err);
        } finally {
            setSearchingClients(false);
        }
    };
    
    // Select client from search
    const selectClient = (client) => {
        setNewChainForm(prev => ({
            ...prev,
            clientName: `${client.first_name} ${client.last_name}`.trim(),
            clientPhone: client.cell_phone || client.home_phone || '',
            clientId: client.client_id,
        }));
        setClientSearchResults([]);
        setClientSearchTerm('');
    };
    
    // Create new communication chain
    const handleCreateChain = async (e) => {
        e.preventDefault();
        if (!newChainForm.clientName || !newChainForm.clientPhone) {
            alert('Client name and phone are required');
            return;
        }
        
        const locationId = selectedLocation || 1;
        
        setLoading(true);
        try {
            await createCommunicationChain({
                clientName: newChainForm.clientName,
                clientPhone: newChainForm.clientPhone,
                clientId: newChainForm.clientId,
                locationId,
                notes: newChainForm.notes,
                initialEvent: {
                    type: newChainForm.eventType,
                    notes: newChainForm.eventNotes,
                },
            });
            
            // Reset form and switch to chains tab
            setNewChainForm({
                clientName: '',
                clientPhone: '',
                clientId: null,
                notes: '',
                eventType: COMMUNICATION_EVENT_TYPES.INBOUND_CALL,
                eventNotes: '',
            });
            setActiveTab('call_chains');
            loadCommunicationChains();
        } catch (err) {
            alert('Failed to create communication chain: ' + err.message);
        } finally {
            setLoading(false);
        }
    };
    
    // Add event to chain
    const handleAddEvent = async () => {
        if (!addEventChainId) return;
        
        setLoading(true);
        try {
            await addCommunicationChainEvent(addEventChainId, {
                type: newEventType,
                notes: newEventNotes,
                userId: currentUser?.user_id,
            });
            
            setShowAddEventModal(false);
            setAddEventChainId(null);
            setNewEventType(COMMUNICATION_EVENT_TYPES.OUTBOUND_CALL);
            setNewEventNotes('');
            loadCommunicationChains();
        } catch (err) {
            alert('Failed to add event: ' + err.message);
        } finally {
            setLoading(false);
        }
    };
    
    // Open email reply modal pre-filled from chain data
    const handleOpenEmailReply = (chain) => {
        setEmailReplyChainId(chain.chain_id);
        setEmailReplyTo(chain.client_email || '');
        // Find the most recent email_received event for subject/message-id threading
        const emailEvents = getEventsNewestFirst(chain.events || []).filter(e => e.type === 'email_received');
        const latestEmail = emailEvents.length > 0 ? emailEvents[0] : null;
        setEmailReplySubject(latestEmail?.subject || '');
        setEmailReplyInReplyTo(latestEmail?.gmail_message_id || null);
        // Determine from address based on chain location
        const LOCATION_EMAIL_MAP = { 1: 'hopkins@zoomgroomllc.com', 2: 'invergrove@zoomgroomllc.com' };
        setEmailReplyFrom(LOCATION_EMAIL_MAP[chain.location_id] || 'hopkins@zoomgroomllc.com');
        setEmailReplyBody('');
        setShowEmailReplyModal(true);
    };
    
    // AI-suggested reply
    const handleSuggestReply = async () => {
        if (!emailReplyChainId) return;
        setSuggestingReply(true);
        setSuggestionError(null);
        try {
            const result = await suggestEmailReply(emailReplyChainId);
            setEmailReplyBody(result.suggestion);
        } catch (err) {
            setSuggestionError(err.message || 'Failed to generate suggestion');
        } finally {
            setSuggestingReply(false);
        }
    };

    // Send email reply
    const handleSendEmailReply = async () => {
        if (!emailReplyChainId || !emailReplyTo || !emailReplyBody) return;
        
        setSendingEmail(true);
        try {
            await sendEmailReply(emailReplyChainId, {
                toEmail: emailReplyTo,
                subject: emailReplySubject,
                body: emailReplyBody,
                inReplyTo: emailReplyInReplyTo,
                userId: currentUser?.user_id,
            });
            
            setShowEmailReplyModal(false);
            setEmailReplyChainId(null);
            setEmailModalPos(null);
            setEmailReplyTo('');
            setEmailReplySubject('');
            setEmailReplyBody('');
            setEmailReplyInReplyTo(null);
            loadCommunicationChains();
        } catch (err) {
            alert('Failed to send email: ' + err.message);
        } finally {
            setSendingEmail(false);
        }
    };
    
    // Update chain status
    const handleUpdateStatus = async (chainId, status) => {
        setLoading(true);
        try {
            await updateCommunicationChainStatus(chainId, status);
            loadCommunicationChains();
        } catch (err) {
            alert('Failed to update status: ' + err.message);
        } finally {
            setLoading(false);
        }
    };
    
    // Mark chain as resolved
    const handleMarkResolved = async (chainId) => {
        setLoading(true);
        try {
            await markCommunicationChainResolved(chainId);
            loadCommunicationChains();
        } catch (err) {
            alert('Failed to mark as resolved: ' + err.message);
        } finally {
            setLoading(false);
        }
    };
    
    // Quote services for a chain
    const handleQuoteServices = async (chain) => {
        setShowQuoteModal(true);
        setQuoteData(null);
        setQuoteError(null);
        setQuoteLoading(true);

        try {
            // Extract dog names from client_name if it has parenthetical pet names
            // e.g. "Katherine Molenaar (Cooper, Tucker)" → ["Cooper", "Tucker"]
            const petMatch = chain.client_name?.match(/\(([^)]+)\)/);
            const dogNames = petMatch
                ? petMatch[1].split(',').map(n => n.trim()).filter(Boolean)
                : null;

            // Gather voicemail transcripts from events
            const transcripts = (chain.events || [])
                .filter(e => e.type === 'voicemail_received' || e.transcript)
                .map(e => e.transcript || e.notes)
                .filter(Boolean);
            const clientMessage = transcripts.length > 0 ? transcripts.join('\n\n') : null;

            // Decide which path: existing client (client_id + dog names) or message-based
            const params = { location_id: chain.location_id || 1 };

            if (chain.client_id && dogNames && dogNames.length > 0) {
                params.client_id = chain.client_id;
                params.dog_names = dogNames;
                // Don't send client_message here — DaySmart history lookup doesn't need LLM parsing
            } else if (clientMessage) {
                params.client_message = clientMessage;
            } else if (chain.client_id) {
                // Have client_id but no dog names extracted — try message path with any notes
                const allNotes = (chain.events || [])
                    .map(e => e.notes)
                    .filter(Boolean)
                    .join('\n\n');
                if (allNotes) {
                    params.client_message = allNotes;
                } else {
                    setQuoteError('No pet names or voicemail transcript available to generate a quote.');
                    setQuoteLoading(false);
                    return;
                }
            } else {
                setQuoteError('No client information available to generate a quote.');
                setQuoteLoading(false);
                return;
            }

            const result = await quoteServices(params);
            setQuoteData(result);
        } catch (err) {
            setQuoteError(err.message);
        } finally {
            setQuoteLoading(false);
        }
    };

    // Initiate outbound call via browser and record communication chain event
    const handleCallClient = async (phone, clientContext) => {
        if (!twilioInitialized) {
            alert('Phone system is still initializing. Please wait.');
            return;
        }
        
        // If there's an active call, hang up first
        if (activeCallPhone) {
            hangupCall();
            setActiveCallPhone(null);
            setCallStatus('ready');
            return;
        }
        
        if (!window.confirm(`Call ${formatPhoneNumber(phone)}?`)) return;
        
        try {
            setActiveCallPhone(phone);
            setCallStatus('ringing');
            await makeCall(phone, (status) => {
                setCallStatus(status);
                if (status === 'disconnected' || status === 'cancelled' || status === 'rejected') {
                    setActiveCallPhone(null);
                    setTimeout(() => setCallStatus('ready'), 2000);
                }
            });
            // Record communication chain event (find existing open chain or create new)
            if (clientContext?.clientName && clientContext?.locationId) {
                try {
                    await recordCommunicationEvent({
                        clientId: clientContext.clientId,
                        clientPhone: phone,
                        clientName: clientContext.clientName,
                        locationId: clientContext.locationId,
                        eventType: COMMUNICATION_EVENT_TYPES.OUTBOUND_CALL,
                        eventNotes: clientContext.eventNotes || 'Outbound call from communication center',
                        userId: currentUser?.user_id,
                        // Note: CallSid is not available from Twilio Client SDK for outbound calls.
                        // The voice handler writes the call_recording_index with CallSid + to_phone
                        // so the recording callback can find and update this chain by phone number.
                    });
                    // Refresh chains list to show the updated event
                    if (isChainTab) loadCommunicationChains();
                } catch (chainErr) {
                    console.error('Failed to record communication chain event:', chainErr);
                }
            }
        } catch (err) {
            alert('Failed to initiate call: ' + err.message);
            setActiveCallPhone(null);
            setCallStatus('ready');
        }
    };
    
    // Bulk actions
    const toggleChainSelection = (chainId) => {
        setSelectedChains(prev => {
            const next = new Set(prev);
            if (next.has(chainId)) next.delete(chainId);
            else next.add(chainId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedChains.size === displayedChains.length) {
            setSelectedChains(new Set());
        } else {
            setSelectedChains(new Set(displayedChains.map(c => c.chain_id)));
        }
    };

    const handleBulkAction = async (action) => {
        if (selectedChains.size === 0) return;
        const label = action === 'archive' ? 'archive' : action === 'delete' ? 'archive' : 'resolve';
        if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selectedChains.size} selected chain(s)?`)) return;

        setLoading(true);
        try {
            await bulkUpdateCommunicationChains([...selectedChains], action);
            setSelectedChains(new Set());
            loadCommunicationChains();
        } catch (err) {
            alert(`Failed to ${label}: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Hang up current call
    const handleHangup = () => {
        hangupCall();
        setActiveCallPhone(null);
        setCallStatus('ready');
    };
    
    // Open availability drawer with booking context from a chain's appointment history
    const handleOpenAvailabilityDrawer = useCallback((context) => {
        setAvailabilityBookingContext(context);
        setAvailabilityDrawerOpen(true);
    }, []);
    
    // Build user_id -> full name map from store employees
    const userIdToFullName = React.useMemo(() => {
        const map = {};
        (storeEmployees || []).forEach((emp) => {
            map[emp.user_id] = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
        });
        return map;
    }, [storeEmployees]);

    // Format timestamp
    const formatTimestamp = (ts) => {
        if (!ts) return '';
        const date = new Date(ts);
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    };

    // Compute human-readable age string + color class for a chain
    const getChainAge = (ts) => {
        if (!ts) return null;
        const diffMs = Date.now() - new Date(ts).getTime();
        if (diffMs < 0) return null;
        const minutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(diffMs / 3600000);
        const days = Math.floor(diffMs / 86400000);

        let label;
        if (minutes < 60) label = `${minutes}m`;
        else if (hours < 24) label = `${hours}h`;
        else if (days < 7) label = `${days}d`;
        else label = `${Math.floor(days / 7)}w`;

        // Color gradient: fresh = yellow, aging = orange, stale = red
        let colorClass;
        if (hours < 4) colorClass = 'bg-yellow-100 text-yellow-800';
        else if (hours < 24) colorClass = 'bg-amber-100 text-amber-800';
        else if (days < 3) colorClass = 'bg-orange-100 text-orange-800';
        else colorClass = 'bg-red-100 text-red-700';

        return { label, colorClass };
    };
    
    // Location name helper
    const getLocationName = (id) => {
        if (id === 1) return 'Hopkins';
        if (id === 2) return 'Inver Grove';
        return 'Unknown';
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/timekeeping')}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-2">
                            <Phone className="w-6 h-6 text-blue-600" />
                            <h1 className="text-xl font-semibold">Communication Center</h1>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        {/* Active call indicator */}
                        {activeCallPhone && (
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                                callStatus === 'connected' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                                <PhoneCall className="w-4 h-4 animate-pulse" />
                                <span className="text-sm font-medium">
                                    {callStatus === 'ringing' ? 'Calling...' : 'Connected'} {formatPhoneNumber(activeCallPhone)}
                                </span>
                                <button
                                    onClick={handleHangup}
                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    title="Hang up"
                                >
                                    <PhoneOff className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                        
                        {/* Phone status indicator */}
                        {!activeCallPhone && (
                            <div className={`flex items-center gap-1 text-sm ${
                                callStatus === 'ready' ? 'text-green-600' : 
                                callStatus === 'initializing' ? 'text-yellow-600' : 'text-gray-400'
                            }`}>
                                <Phone className="w-4 h-4" />
                                {callStatus === 'ready' ? 'Ready' : 
                                 callStatus === 'initializing' ? 'Connecting...' : 'Offline'}
                            </div>
                        )}
                        
                        {/* Location filter */}
                        <select
                            value={selectedLocation || ''}
                            onChange={(e) => setSelectedLocation(e.target.value ? parseInt(e.target.value) : null)}
                            className="border rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="">All Locations</option>
                            <option value="1">Hopkins</option>
                            <option value="2">Inver Grove</option>
                        </select>
                        
                        {/* Refresh */}
                        <button
                            onClick={() => loadCommunicationChains()}
                            disabled={loading}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            </header>
            
            {/* Tabs */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4">
                    <nav className="flex gap-4">
                        <button
                            onClick={() => setActiveTab('call_chains')}
                            className={`py-3 px-4 border-b-2 font-medium text-sm ${
                                activeTab === 'call_chains'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                Call Chains
                                {openCallCount > 0 && (
                                    <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                        {openCallCount}
                                    </span>
                                )}
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('email_chains')}
                            className={`py-3 px-4 border-b-2 font-medium text-sm ${
                                activeTab === 'email_chains'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                Email Chains
                                {openEmailCount > 0 && (
                                    <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                        {openEmailCount}
                                    </span>
                                )}
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('groombot')}
                            className={`py-3 px-4 border-b-2 font-medium text-sm ${
                                activeTab === 'groombot'
                                    ? 'border-emerald-600 text-emerald-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Bot className="w-4 h-4" />
                                Zoom Bot
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('new')}
                            className={`py-3 px-4 border-b-2 font-medium text-sm ${
                                activeTab === 'new'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Plus className="w-4 h-4" />
                                New Chain
                            </div>
                        </button>
                    </nav>
                </div>
            </div>
            
            {/* Content */}
            <main className="max-w-7xl mx-auto px-4 py-6">
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                        {error}
                    </div>
                )}

                        {/* Call Chains / Email Chains Tab */}
                        {isChainTab && (
                            <div>
                                {/* Status filter + bulk actions */}
                                <div className="mb-4 flex flex-wrap items-center gap-2">
                                    <button
                                        onClick={() => setActiveStatusFilters(new Set(allStatuses))}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                            allFiltersActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                                        }`}
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => toggleStatusFilter(COMMUNICATION_CHAIN_STATUS.OPEN)}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                            activeStatusFilters.has(COMMUNICATION_CHAIN_STATUS.OPEN) ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-500'
                                        }`}
                                    >
                                        Open
                                    </button>
                                    <button
                                        onClick={() => toggleStatusFilter(COMMUNICATION_CHAIN_STATUS.PENDING_RESPONSE)}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                            activeStatusFilters.has(COMMUNICATION_CHAIN_STATUS.PENDING_RESPONSE) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                                        }`}
                                    >
                                        Pending Response
                                    </button>
                                    <button
                                        onClick={() => toggleStatusFilter(COMMUNICATION_CHAIN_STATUS.RESOLVED)}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                            activeStatusFilters.has(COMMUNICATION_CHAIN_STATUS.RESOLVED) ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                                        }`}
                                    >
                                        Resolved
                                    </button>
                                    <button
                                        onClick={() => toggleStatusFilter(COMMUNICATION_CHAIN_STATUS.ARCHIVED)}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                            activeStatusFilters.has(COMMUNICATION_CHAIN_STATUS.ARCHIVED) ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-500'
                                        }`}
                                    >
                                        Archived
                                    </button>

                                    {/* Divider */}
                                    {displayedChains.length > 0 && (
                                        <span className="mx-1 h-6 w-px bg-gray-300" />
                                    )}

                                    {/* Select All */}
                                    {displayedChains.length > 0 && (
                                        <button
                                            onClick={toggleSelectAll}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        >
                                            {selectedChains.size === displayedChains.length && displayedChains.length > 0
                                                ? <CheckSquare className="w-4 h-4" />
                                                : <Square className="w-4 h-4" />}
                                            {selectedChains.size === displayedChains.length && displayedChains.length > 0
                                                ? 'Deselect All'
                                                : 'Select All'}
                                        </button>
                                    )}

                                    {/* Search + client filter */}
                                    <div className="relative ml-auto">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search by name, phone, or email..."
                                            value={chainSearchTerm}
                                            onChange={(e) => setChainSearchTerm(e.target.value)}
                                            className="pl-8 pr-8 py-1.5 rounded-full text-sm border border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none w-64"
                                        />
                                        {chainSearchTerm && (
                                            <button
                                                onClick={() => setChainSearchTerm('')}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Bulk action buttons */}
                                    {selectedChains.size > 0 && (
                                        <>
                                            <button
                                                onClick={() => handleBulkAction('resolve')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-600 text-white hover:bg-green-700"
                                            >
                                                <CheckCircle className="w-4 h-4" />
                                                Resolve Selected ({selectedChains.size})
                                            </button>
                                            <button
                                                onClick={() => handleBulkAction('archive')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-600 text-white hover:bg-gray-700"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                                Archive Selected ({selectedChains.size})
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Active client filter chip */}
                                {clientFilter && (
                                    <div className="mb-3 flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                                            <User className="w-3.5 h-3.5" />
                                            Showing chains for: {clientFilter.clientName}
                                            <button
                                                onClick={() => {
                                                    setClientFilter(null);
                                                    setActiveStatusFilters(new Set([COMMUNICATION_CHAIN_STATUS.OPEN, COMMUNICATION_CHAIN_STATUS.PENDING_RESPONSE]));
                                                }}
                                                className="ml-1 hover:text-purple-600"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </span>
                                    </div>
                                )}
                                
                                {/* Chains list */}
                                {(loading || searchPending) && displayedChains.length === 0 ? (
                                    <div className="flex justify-center py-12">
                                        <LoadingSpinner />
                                    </div>
                                ) : communicationChains.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500">
                                        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>{debouncedChainSearch ? 'No matching communications found' : 'No communication chains found'}</p>
                                        {!debouncedChainSearch && (
                                            <button
                                                onClick={() => setActiveTab('new')}
                                                className="mt-4 text-blue-600 hover:underline"
                                            >
                                                Create your first chain
                                            </button>
                                        )}
                                    </div>
                                ) : displayedChains.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500">
                                        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>No chains match the selected filters</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {displayedChains.map((chain) => {
                                            const timelineEvents = getEventsNewestFirst(chain.events || []);
                                            const isTimelineExpanded = expandedTimelineChains.has(chain.chain_id);
                                            const visibleTimelineEvents = isTimelineExpanded
                                                ? timelineEvents
                                                : timelineEvents.slice(0, COLLAPSED_TIMELINE_EVENT_LIMIT);
                                            const hiddenTimelineEventCount = Math.max(timelineEvents.length - COLLAPSED_TIMELINE_EVENT_LIMIT, 0);
                                            return (
                                            <div
                                                key={chain.chain_id}
                                                className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
                                                    selectedChains.has(chain.chain_id) ? 'ring-2 ring-blue-400' : ''
                                                }`}
                                            >
                                                {/* Chain header */}
                                                <div
                                                    className="p-4 cursor-pointer hover:bg-gray-50"
                                                    onClick={() => setExpandedChain(
                                                        expandedChain === chain.chain_id ? null : chain.chain_id
                                                    )}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-4">
                                                            {/* Checkbox */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleChainSelection(chain.chain_id);
                                                                }}
                                                                className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                                                            >
                                                                {selectedChains.has(chain.chain_id)
                                                                    ? <CheckSquare className="w-5 h-5 text-blue-600" />
                                                                    : <Square className="w-5 h-5" />}
                                                            </button>
                                                            <div>
                                                                <h3 className="font-medium">
                                                                    {chain.client_name}
                                                                    {chain.client_id && (
                                                                        <a
                                                                            href={`https://cloud.daysmartpet.com/beta/Client/${chain.client_id}/history/upcoming`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            className="ml-2 text-xs text-blue-600 hover:text-blue-800 hover:underline font-normal"
                                                                        >
                                                                            View in DaySmart →
                                                                        </a>
                                                                    )}
                                                                    {(chain.client_id || chain.client_phone) && !clientFilter && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setClientFilter({
                                                                                    clientId: chain.client_id,
                                                                                    clientName: chain.client_name,
                                                                                    clientPhone: chain.client_phone,
                                                                                });
                                                                                setActiveStatusFilters(new Set([...allStatuses, COMMUNICATION_CHAIN_STATUS.ARCHIVED]));
                                                                            }}
                                                                            className="ml-2 text-xs text-purple-600 hover:text-purple-800 hover:underline font-normal"
                                                                        >
                                                                            View all chains →
                                                                        </button>
                                                                    )}
                                                                </h3>
                                                                <p className="text-sm text-gray-500">
                                                                    {formatPhoneNumber(chain.client_phone)}
                                                                    {chain.client_email ? ` · ${chain.client_email}` : ""}
                                                                </p>
                                                                <ClientLinkNotice
                                                                    chain={chain}
                                                                    userId={currentUser?.user_id}
                                                                    onChanged={() => loadCommunicationChains()}
                                                                />
                                                            </div>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(chain.status)}`}>
                                                                {chain.status.replace('_', ' ')}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-4">
                                                            <div className="text-sm text-gray-500 text-right">
                                                                <div className="flex items-center gap-1">
                                                                    <MapPin className="w-3 h-3" />
                                                                    {getLocationName(chain.location_id)}
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <Clock className="w-3 h-3" />
                                                                    {formatTimestamp(chain.updated_at)}
                                                                    {getChainAge(chain.updated_at) && (
                                                                        <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${getChainAge(chain.updated_at).colorClass}`}>
                                                                            {getChainAge(chain.updated_at).label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (isWeakNameLink(chain)) return;
                                                                    handleCallClient(chain.client_phone, {
                                                                        clientId: chain.client_id,
                                                                        clientName: chain.client_name,
                                                                        locationId: chain.location_id,
                                                                    });
                                                                }}
                                                                disabled={isWeakNameLink(chain) || !chain.client_phone}
                                                                className={`p-2 rounded-lg ${
                                                                    isWeakNameLink(chain) || !chain.client_phone
                                                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                        : "bg-green-100 text-green-700 hover:bg-green-200"
                                                                }`}
                                                                title={isWeakNameLink(chain) ? "Confirm the client before calling" : "Call client"}
                                                            >
                                                                <PhoneOutgoing className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDetailChain(chain);
                                                                }}
                                                                className="p-2 bg-transparent border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 text-gray-400 hover:text-gray-600 transition-colors"
                                                                title="View details"
                                                            >
                                                                <Settings className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Expanded content - 50/50 split: Timeline | Appointments */}
                                                {expandedChain === chain.chain_id && (
                                                    <div className="border-t bg-gray-50 p-4">
                                                        {/* Notes (full width above the split) */}
                                                        {chain.notes && (
                                                            <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-sm">
                                                                <strong>Notes:</strong> {chain.notes}
                                                            </div>
                                                        )}

                                                        <div className="flex gap-4">
                                                            {/* LEFT: Timeline + Actions */}
                                                            <div className="w-1/2 min-w-0">
                                                                {/* Actions */}
                                                                <div className="flex gap-2 flex-wrap mb-4">
                                                                    <button
                                                                        onClick={() => {
                                                                            setAddEventChainId(chain.chain_id);
                                                                            setShowAddEventModal(true);
                                                                        }}
                                                                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                                                                    >
                                                                        <Plus className="w-4 h-4 inline mr-1" />
                                                                        Add Event
                                                                    </button>
                                                                    {chain.client_email && (
                                                                        <button
                                                                            onClick={() => handleOpenEmailReply(chain)}
                                                                            className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                                                                        >
                                                                            <Mail className="w-4 h-4 inline mr-1" />
                                                                            Reply via Email
                                                                        </button>
                                                                    )}
                                                                    {chain.status !== COMMUNICATION_CHAIN_STATUS.RESOLVED && (
                                                                        <button
                                                                            onClick={() => handleMarkResolved(chain.chain_id)}
                                                                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                                                                        >
                                                                            <CheckCircle className="w-4 h-4 inline mr-1" />
                                                                            ✓ Mark Resolved
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleQuoteServices(chain)}
                                                                        className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
                                                                    >
                                                                        <DollarSign className="w-4 h-4 inline mr-1" />
                                                                        Quote Services
                                                                    </button>
                                                                    {chain.status === COMMUNICATION_CHAIN_STATUS.RESOLVED && (
                                                                        <button
                                                                            onClick={() => handleUpdateStatus(chain.chain_id, COMMUNICATION_CHAIN_STATUS.OPEN)}
                                                                            className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-700"
                                                                        >
                                                                            Reopen
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {/* Events timeline */}
                                                                <div className="mb-4">
                                                                    <h4 className="font-medium mb-2">Timeline</h4>
                                                                    {chain.events && chain.events.length > 0 ? (
                                                                        <div className="space-y-2">
                                                                            {visibleTimelineEvents.map((event, idx) => {
                                                                                const info = getCommunicationEventInfo(event);
                                                                                const isCallRecording = event.audio_bucket === 'groombot_conversations';
                                                                                const isLoggedNote = isManualEmailNote(event);
                                                                                const isEmailEvent = !isLoggedNote && (event.type === 'email_received' || event.type === 'email_sent');
                                                                                const emailContent = isEmailEvent ? getEmailEventContent(event) : null;
                                                                                const eventNotes = isEmailEvent ? emailContent.body : event.notes;
                                                                                const salonEmail = chain.location_id === 2 ? 'invergrove@zoomgroomllc.com' : 'hopkins@zoomgroomllc.com';
                                                                                return (
                                                                                    <div key={idx} className="flex items-start gap-3 text-sm">
                                                                                        <span className="text-lg">{info.icon}</span>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <span className={`font-medium ${info.color}`}>
                                                                                                {info.label}
                                                                                            </span>
                                                                                            {event.user_id && userIdToFullName[event.user_id] && (
                                                                                                <span className="text-gray-500"> - {userIdToFullName[event.user_id]}</span>
                                                                                            )}
                                                                                            {isEmailEvent && (
                                                                                                <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                                                                                                    {event.type === 'email_received' && (
                                                                                                        <>
                                                                                                            <div><span className="font-medium text-gray-600">From:</span> {event.sender_email || chain.client_email || 'unknown'}</div>
                                                                                                            <div><span className="font-medium text-gray-600">To:</span> {salonEmail}</div>
                                                                                                        </>
                                                                                                    )}
                                                                                                    {event.type === 'email_sent' && (
                                                                                                        <>
                                                                                                            <div><span className="font-medium text-gray-600">From:</span> {salonEmail}</div>
                                                                                                            <div><span className="font-medium text-gray-600">To:</span> {event.recipient_email || chain.client_email || 'unknown'}</div>
                                                                                                        </>
                                                                                                    )}
                                                                                                    {emailContent.subject && (
                                                                                                        <div><span className="font-medium text-gray-600">Subject:</span> {emailContent.subject}</div>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}
                                                                                            {eventNotes && (
                                                                                                isLoggedNote ? (
                                                                                                    <div className="mt-1 text-gray-700 text-xs bg-gray-100 border border-gray-200 rounded p-2">
                                                                                                        <span className="font-medium">Note:</span> {eventNotes}
                                                                                                    </div>
                                                                                                ) : eventNotes.includes('\n') ? (
                                                                                                    <div className="mt-1 text-gray-600 text-xs whitespace-pre-wrap bg-white border rounded p-2">
                                                                                                        {eventNotes}
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <span className="text-gray-600"> - {eventNotes}</span>
                                                                                                )
                                                                                            )}
                                                                                            <div className="text-xs text-gray-400">
                                                                                                {formatTimestamp(event.timestamp)}
                                                                                            </div>
                                                                                            {event.audio_blob_name && (
                                                                                                <VoicemailPlayer
                                                                                                    blobName={event.audio_blob_name}
                                                                                                    bucket={event.audio_bucket}
                                                                                                    label={isCallRecording ? 'Play call recording' : 'Play voicemail'}
                                                                                                />
                                                                                            )}
                                                                                            {event.transcript && (
                                                                                                <details className="mt-1">
                                                                                                    <summary className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                                                                                                        Show transcript
                                                                                                    </summary>
                                                                                                    <div className="mt-1 p-2 bg-white rounded border text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                                                                                                        {event.transcript}
                                                                                                    </div>
                                                                                                </details>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            {hiddenTimelineEventCount > 0 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => toggleTimelineExpansion(chain.chain_id)}
                                                                                    className="w-full rounded border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                                                                                >
                                                                                    {isTimelineExpanded
                                                                                        ? 'Hide older timeline items'
                                                                                        : `Show ${hiddenTimelineEventCount} older timeline item${hiddenTimelineEventCount === 1 ? '' : 's'}`}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-gray-500 text-sm">No events yet</p>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Client email display */}
                                                                {chain.client_email && (
                                                                    <div className="mb-4 text-sm text-gray-600">
                                                                        <Mail className="w-3.5 h-3.5 inline mr-1" />
                                                                        {chain.client_email}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* RIGHT: Client Appointments Panel */}
                                                            <div className="w-1/2 min-w-0 border-l border-gray-200 pl-4">
                                                                <ClientAppointmentsPanel
                                                                    clientId={chain.client_id}
                                                                    locationId={chain.location_id}
                                                                    clientName={chain.client_name}
                                                                    compact
                                                                    onOpenBooking={handleOpenAvailabilityDrawer}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {loadingMore && (
                                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Loading older communications...
                                    </div>
                                )}
                                {hasMoreChains && !loading && displayedChains.length > 0 && (
                                    <div ref={loadMoreSentinelRef} className="h-8" aria-hidden="true" />
                                )}
                                {!loading && !loadingMore && !hasMoreChains && displayedChains.length > 0 && (
                                    <p className="text-center text-sm text-gray-400 py-6">End of results</p>
                                )}
                            </div>
                        )}
                        
                        {/* Zoom Bot Tab */}
                        {activeTab === 'groombot' && (
                            <ZoomBotAppointmentsPanel
                                locationId={selectedLocation !== DAYSMART_LOCATIONS.ALL ? selectedLocation : null}
                                onCallClient={handleCallClient}
                                onOpenChainDetail={setDetailChain}
                                onAddEvent={(chainId) => {
                                    setAddEventChainId(chainId);
                                    setShowAddEventModal(true);
                                }}
                                onMarkResolved={handleMarkResolved}
                                onUpdateStatus={handleUpdateStatus}
                                userIdToFullName={userIdToFullName}
                                formatTimestamp={formatTimestamp}
                            />
                        )}
                        
                        {/* New Chain Tab */}
                        {activeTab === 'new' && (
                            <div className="max-w-2xl mx-auto">
                                <div className="bg-white rounded-lg shadow-sm border p-6">
                                    <h2 className="text-lg font-semibold mb-4">Create New Communication Chain</h2>
                                    
                                    {/* Client search */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium mb-2">
                                            Search for client (optional)
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={clientSearchTerm}
                                                onChange={(e) => setClientSearchTerm(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleClientSearch()}
                                                placeholder="Search by name or phone..."
                                                className="flex-1 border rounded-lg px-3 py-2"
                                            />
                                            <button
                                                onClick={handleClientSearch}
                                                disabled={searchingClients}
                                                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                                            >
                                                <Search className="w-4 h-4" />
                                            </button>
                                        </div>
                                        
                                        {/* Search results */}
                                        {clientSearchResults.length > 0 && (
                                            <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                                                {clientSearchResults.map((client) => (
                                                    <button
                                                        key={`${client.location_id}-${client.client_id}`}
                                                        onClick={() => selectClient(client)}
                                                        className="w-full text-left p-3 hover:bg-gray-50 border-b last:border-b-0"
                                                    >
                                                        <div className="font-medium">
                                                            {client.first_name} {client.last_name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {formatPhoneNumber(client.cell_phone || client.home_phone)} • {getLocationName(client.location_id)}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <form onSubmit={handleCreateChain}>
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Client Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={newChainForm.clientName}
                                                    onChange={(e) => setNewChainForm(prev => ({ ...prev, clientName: e.target.value }))}
                                                    required
                                                    className="w-full border rounded-lg px-3 py-2"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Phone Number *
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={newChainForm.clientPhone}
                                                    onChange={(e) => setNewChainForm(prev => ({ ...prev, clientPhone: e.target.value }))}
                                                    required
                                                    className="w-full border rounded-lg px-3 py-2"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium mb-1">
                                                Notes
                                            </label>
                                            <textarea
                                                value={newChainForm.notes}
                                                onChange={(e) => setNewChainForm(prev => ({ ...prev, notes: e.target.value }))}
                                                rows={2}
                                                className="w-full border rounded-lg px-3 py-2"
                                                placeholder="Any notes about this client..."
                                            />
                                        </div>
                                        
                                        <div className="border-t pt-4 mt-4">
                                            <h3 className="font-medium mb-3">Initial Event</h3>
                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Event Type
                                                    </label>
                                                    <select
                                                        value={newChainForm.eventType}
                                                        onChange={(e) => setNewChainForm(prev => ({ ...prev, eventType: e.target.value }))}
                                                        className="w-full border rounded-lg px-3 py-2"
                                                    >
                                                        {Object.entries(COMMUNICATION_EVENT_TYPES).map(([key, value]) => (
                                                            <option key={key} value={value}>
                                                                {getEventTypeInfo(value).icon} {getEventTypeInfo(value).label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Event Notes
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={newChainForm.eventNotes}
                                                        onChange={(e) => setNewChainForm(prev => ({ ...prev, eventNotes: e.target.value }))}
                                                        className="w-full border rounded-lg px-3 py-2"
                                                        placeholder="What happened..."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {loading ? 'Creating...' : 'Create Communication Chain'}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
            </main>
            
            {/* Add Event Modal */}
            {showAddEventModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">Add Event</h3>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Event Type</label>
                            <select
                                value={newEventType}
                                onChange={(e) => setNewEventType(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                            >
                                {Object.entries(COMMUNICATION_EVENT_TYPES).map(([key, value]) => (
                                    <option key={key} value={value}>
                                        {getEventTypeInfo(value).icon} {getEventTypeInfo(value).label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-1">Notes</label>
                            <textarea
                                value={newEventNotes}
                                onChange={(e) => setNewEventNotes(e.target.value)}
                                rows={3}
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="What happened..."
                            />
                        </div>
                        
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setShowAddEventModal(false);
                                    setAddEventChainId(null);
                                }}
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddEvent}
                                disabled={loading}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? 'Adding...' : 'Add Event'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Email Reply Modal */}
            {showEmailReplyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-30 z-50">
                    <div
                        ref={emailModalRef}
                        className="bg-white rounded-lg shadow-2xl flex flex-col fixed"
                        style={{
                            top: emailModalPos ? emailModalPos.y : '50%',
                            left: emailModalPos ? emailModalPos.x : '50%',
                            transform: emailModalPos ? 'none' : 'translate(-50%, -50%)',
                            width: 520,
                            height: 560,
                            minWidth: 360,
                            minHeight: 420,
                            maxWidth: '95vw',
                            maxHeight: '95vh',
                            resize: 'both',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Draggable header */}
                        <div
                            onMouseDown={handleEmailModalDragStart}
                            className="flex items-center gap-2 px-5 py-3 border-b bg-gray-50 rounded-t-lg cursor-move select-none shrink-0"
                        >
                            <Mail className="w-4 h-4 text-purple-600" />
                            <h3 className="text-sm font-semibold flex-1">Reply via Email</h3>
                            <button
                                onClick={() => { setShowEmailReplyModal(false); setEmailReplyChainId(null); setEmailModalPos(null); }}
                                className="text-gray-400 hover:text-gray-600 p-0.5"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Form content */}
                        <div className="flex-1 overflow-y-auto p-5 flex flex-col min-h-0 gap-3">
                            <div>
                                <label className="block text-sm font-medium mb-1">From</label>
                                <input
                                    type="email"
                                    value={emailReplyFrom}
                                    readOnly
                                    className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">To</label>
                                <input
                                    type="email"
                                    value={emailReplyTo}
                                    onChange={(e) => setEmailReplyTo(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                    placeholder="client@example.com"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Subject</label>
                                <input
                                    type="text"
                                    value={emailReplySubject}
                                    onChange={(e) => setEmailReplySubject(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                    placeholder="Re: ..."
                                />
                            </div>

                            <div className="flex-1 flex flex-col min-h-0">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium">Message</label>
                                    <button
                                        onClick={handleSuggestReply}
                                        disabled={suggestingReply || sendingEmail}
                                        className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {suggestingReply ? (
                                            <>
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                Thinking...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-3.5 h-3.5" />
                                                Suggest a Response
                                            </>
                                        )}
                                    </button>
                                </div>
                                {suggestionError && (
                                    <div className="mb-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                                        {suggestionError}
                                    </div>
                                )}
                                <textarea
                                    value={emailReplyBody}
                                    onChange={(e) => setEmailReplyBody(e.target.value)}
                                    className="w-full flex-1 min-h-[120px] border rounded-lg px-3 py-2 text-sm"
                                    placeholder="Type your reply or click 'Suggest a Response' to draft with AI..."
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex gap-2 px-5 py-3 border-t shrink-0">
                            <button
                                onClick={() => { setShowEmailReplyModal(false); setEmailReplyChainId(null); setEmailModalPos(null); }}
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm"
                                disabled={sendingEmail}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSendEmailReply}
                                disabled={sendingEmail || !emailReplyTo || !emailReplyBody}
                                className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                            >
                                {sendingEmail ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Mail className="w-4 h-4" />
                                        Send Email
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Sticky Availability Drawer */}
            <StickyAvailabilityDrawer
                isOpen={availabilityDrawerOpen}
                onClose={() => setAvailabilityDrawerOpen(false)}
                bookingContext={availabilityBookingContext}
                onBookingComplete={() => {
                    // Refresh chains to reflect new booking
                    if (isChainTab) loadCommunicationChains();
                }}
            />

            {/* Chain Detail Modal */}
            {detailChain && (
                <ChainDetailModal
                    chain={detailChain}
                    onClose={() => setDetailChain(null)}
                />
            )}

            {/* Quote Result Modal */}
            <QuoteResultModal
                show={showQuoteModal}
                onClose={() => setShowQuoteModal(false)}
                quoteData={quoteData}
                loading={quoteLoading}
                error={quoteError}
            />
        </div>
    );
};

export default CommunicationCenter;
